<script setup lang="ts">
    import { DEFAULT_AVATAR } from '@/constants/image';
    import AdvancedImage from '@shared/components/ui/advanced-image.vue';
    import { type MediaTyping } from '@shared/typings';

    defineProps<{
        cover?: MediaTyping;
    }>();

    const RESIZE_TIMEOUT = 250;

    const emit = defineEmits<{(event: 'changed', value?: HTMLElement)}>();

    const intersectionObserver: Ref<IntersectionObserver|undefined> = ref();
    const mutationObserver: Ref<MutationObserver|undefined> = ref();
    const resizeObserver: Ref<ResizeObserver|undefined> = ref();
    const target: Ref<HTMLDivElement|undefined> = ref();
    const transitioning: Ref<boolean> = ref(false);
    const resizeTimeout: Ref<Timer|undefined> = ref();

    const logElementPosition = (): void => {
        emit('changed', target.value);
    };

    const onMutationObserver: MutationCallback = (mutations): void => {
        mutations.forEach(_mutation => {
            logElementPosition();
        });
    };

    const trackTransition = (): void => {
        if (transitioning.value) {
            logElementPosition();
            requestAnimationFrame(trackTransition);
        } else {
            transitioning.value = false;
        }
    };

    const onTargetTransitionStarted = (): void => {
        transitioning.value = true;
        requestAnimationFrame(trackTransition);
    };

    const onTargetTransitionEnded = (): void => {
        transitioning.value = false;
    };

    const destroyTargetTransitionListener = (): void => {
        transitioning.value = false;
        if (target.value?.parentElement) {
            target.value?.parentElement.removeEventListener('transitionstart', onTargetTransitionStarted);
            target.value?.parentElement.removeEventListener('transitionend', onTargetTransitionEnded);
        }
    };

    const createTransitionListener = (): void => {
        if (target.value?.parentElement) {
            target.value?.parentElement.addEventListener('transitionstart', onTargetTransitionStarted);
            target.value?.parentElement.addEventListener('transitionend', onTargetTransitionEnded);
        }
    };

    const onWindowResize = (): void => {
        clearTimeout(resizeTimeout.value);
        resizeTimeout.value = setTimeout(() => {
            logElementPosition();
        }, RESIZE_TIMEOUT);
    };

    const createScrollParentListener = (): void => {
        if (target.value) {
            window.addEventListener('scroll', logElementPosition);
            window.addEventListener('resize', onWindowResize);
        }
    };

    const createIntersectionObserver = (): void => {
        if (target.value?.parentElement) {
            intersectionObserver.value = new IntersectionObserver(logElementPosition);
            intersectionObserver.value.observe(target.value?.parentElement);
        }
    };

    const createResizeObserver = (): void => {
        try {
            if (target.value?.parentElement) {
                resizeObserver.value = new ResizeObserver(logElementPosition);
                resizeObserver.value.observe(target.value.parentElement);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('unable to initialize resize observer', error);
        }
    };

    const createMutationObserver = (): void => {
        if (target.value?.parentElement) {
            mutationObserver.value = new MutationObserver(onMutationObserver);
            mutationObserver.value?.observe(target.value.parentElement, { attributes: true });
        }
    };

    const destroyResizeObserver = (): void => {
        if (!resizeObserver.value || !target.value?.parentElement) return;
        try {
            resizeObserver.value.unobserve(target.value.parentElement);
            resizeObserver.value.disconnect();
        } catch {
            // Safari / Chromium teardown bug — ignore safely
        } finally {
            // eslint-disable-next-line no-undefined
            resizeObserver.value = undefined;
        }
    };

    const destroyMutationObserver = (): void => {
        if (typeof mutationObserver.value?.disconnect === 'function') {
            mutationObserver.value.disconnect();
        }
    };

    const destroyIntersectionObserver = (): void => {
        if (typeof intersectionObserver.value?.disconnect === 'function') {
            intersectionObserver.value.disconnect();
        }
    };

    const destroyScrollParentListener = (): void => {
        if (target.value) {
            clearTimeout(resizeTimeout.value);
            window.removeEventListener('scroll', logElementPosition);
            window.removeEventListener('resize', onWindowResize);
        }
    };

    onMounted(async() => {
        await nextTick();
        createScrollParentListener();
        createIntersectionObserver();
        createResizeObserver();
        createMutationObserver();
        createTransitionListener();
    });

    onBeforeUnmount(() => {
        destroyTargetTransitionListener();
        destroyResizeObserver();
        destroyMutationObserver();
        destroyIntersectionObserver();
        destroyScrollParentListener();
    });
</script>

<template>
    <div
        ref="target"
        class="venue-host-img"
    >
        <AdvancedImage
            aspect-ratio="1:1"
            :preview="cover ? null : DEFAULT_AVATAR"
            :media="cover"
        />
    </div>
</template>

<style lang="scss" scoped>
.venue-host-img {
    position: relative;
    overflow: hidden;
    border:  solid 1px var(--border-primary);
    border-radius: var(--size-2);
    aspect-ratio: 1/1;
    inline-size: 100%;
    transition: all 1s ease-in-out;
}
</style>
